import { useState } from 'react'
import { CreditCard, ExternalLink } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import type {
  InvoiceDetailResponse,
  InvoiceHeader,
  InvoiceLine,
  InvoicePayment,
  PayLinkResponse,
} from '@/lib/api-types'
import { formatDate, formatMoney, formatQty } from '@/lib/format'
import { paymentChipClasses } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { DescriptionList, type DescriptionItem } from '@/components/ui/description-list'
import { Dialog } from '@/components/ui/dialog'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Spinner } from '@/components/ui/spinner'
import { StatTile } from '@/components/ui/stat-tile'
import { StatusChip } from '@/components/ui/status-chip'

export function InvoiceDetailPage() {
  const { id = '' } = useParams()

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['invoice', id],
    queryFn: ({ signal }) => api.getInvoice(id, { signal }),
    staleTime: 15_000,
  })

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading this invoice…" />
      </div>
    )
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404
    return (
      <div className="py-24 text-center">
        <p className="micro-label">{missing ? 'Not found' : 'Something went wrong'}</p>
        <p className="mt-3 text-[0.9375rem] text-inkMid">
          {missing
            ? 'This invoice doesn’t exist — or it belongs to another account.'
            : 'We couldn’t load this invoice.'}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {!missing ? (
            <Button variant="neutral" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          ) : null}
          <Button asChild variant="quiet" size="sm">
            <Link to="/invoices">Back to invoices</Link>
          </Button>
        </div>
      </div>
    )
  }

  return <InvoiceDetailView detail={data} />
}

const LINE_COLUMNS: DataTableColumn<InvoiceLine & { currency: string }>[] = [
  {
    id: 'item',
    header: 'Item',
    flex: true,
    widthClassName: 'min-w-48',
    cell: (line) => (
      <span className="flex flex-col">
        <span
          className="truncate text-[0.9375rem] font-medium text-ink"
          title={line.description ?? line.itemCode ?? undefined}
        >
          {line.description ?? line.itemCode ?? '—'}
        </span>
        {line.itemCode && line.description ? (
          <span className="mt-0.5 truncate font-mono text-xs text-mute">{line.itemCode}</span>
        ) : null}
      </span>
    ),
  },
  {
    id: 'quantity',
    header: 'Quantity',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => (
      <span>
        {formatQty(line.quantity)}
        {line.uom ? <span className="text-mute"> {line.uom}</span> : null}
      </span>
    ),
  },
  {
    id: 'unitPrice',
    header: 'Unit price',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => (line.unitPrice != null ? formatMoney(line.unitPrice, line.currency) : '—'),
  },
  {
    id: 'total',
    header: 'Total',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => (line.lineTotal != null ? formatMoney(line.lineTotal, line.currency) : '—'),
  },
]

const PAYMENT_COLUMNS: DataTableColumn<InvoicePayment & { currency: string }>[] = [
  {
    id: 'date',
    header: 'Date',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (p) => <span className="text-sm text-inkMid">{formatDate(p.date)}</span>,
  },
  {
    id: 'method',
    header: 'Method',
    flex: true,
    widthClassName: 'min-w-36',
    cell: (p) => (
      <span className="text-sm text-inkMid" title={p.method ?? undefined}>
        {p.method ?? '—'}
      </span>
    ),
  },
  {
    id: 'amount',
    header: 'Amount',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (p) => formatMoney(p.amount, p.currency),
  },
]

function InvoiceDetailView({ detail }: { detail: InvoiceDetailResponse }) {
  const { header, lines, payments, relatedOrderIds } = detail
  const overdue = header.paymentGroup === 'overdue'

  const facts: DescriptionItem[] = [
    {
      label: 'Issued',
      value: <time dateTime={header.docDate ?? undefined}>{formatDate(header.docDate)}</time>,
      mono: true,
    },
  ]
  if (header.dueDate) {
    facts.push({
      label: 'Due',
      value: (
        <time
          dateTime={header.dueDate}
          className={cn(overdue && 'font-medium text-amber')}
        >
          {formatDate(header.dueDate)}
        </time>
      ),
      mono: true,
    })
  }

  return (
    <article className="mx-auto max-w-data">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/invoices"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Invoices
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          {header.docNum ?? '—'}
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title={`Invoice ${header.docNum ?? '—'}`}
        actions={<PayByCard header={header} />}
        meta={
          <StatusChip
            label={header.paymentStatusLabel}
            chipClasses={paymentChipClasses(header.paymentGroup)}
          />
        }
      />

      <DescriptionList className="mt-5 border-y border-rule/60 py-4" columns={3} items={facts} />

      {/* Totals */}
      <div className="mt-8 grid gap-3 sm:grid-cols-3">
        <StatTile label="Invoice total" value={formatMoney(header.docTotal, header.currency)} />
        <StatTile
          label="Paid to date"
          value={formatMoney(header.paidToDate ?? 0, header.currency)}
        />
        <StatTile
          label="Balance due"
          value={formatMoney(header.balanceDue ?? 0, header.currency)}
          emphasis={overdue}
          hint={overdue ? 'Past due' : undefined}
        />
      </div>

      {/* Lines */}
      <section aria-labelledby="invoice-lines-heading" className="mt-10">
        <SectionHeader id="invoice-lines-heading" title="Lines" />
        <div className="mt-4">
          <DataTable
            columns={LINE_COLUMNS}
            rows={lines.map((line) => ({ ...line, currency: header.currency }))}
            rowKey={(line) => line.id}
            caption="Invoice lines"
          />
        </div>
      </section>

      {/* Payments */}
      <section aria-labelledby="invoice-payments-heading" className="mt-10">
        <SectionHeader id="invoice-payments-heading" title="Payments" />
        {payments.length === 0 ? (
          <p className="mt-4 text-sm text-mute">No payments applied yet.</p>
        ) : (
          <div className="mt-4 max-w-lg">
            <DataTable
              columns={PAYMENT_COLUMNS}
              rows={payments.map((p) => ({ ...p, currency: header.currency }))}
              rowKey={(p) => p.id}
              caption="Payments applied to this invoice"
            />
          </div>
        )}
      </section>

      {/* Related orders */}
      {relatedOrderIds.length > 0 ? (
        <section aria-labelledby="invoice-orders-heading" className="mt-10">
          <SectionHeader id="invoice-orders-heading" title="Related orders" />
          <ul className="mt-4 flex flex-wrap gap-2">
            {relatedOrderIds.map((orderId) => (
              <li key={orderId}>
                <Link
                  to={`/orders/${orderId}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-rule/80 bg-card px-3 py-2 text-sm font-medium text-inkSoft no-underline transition-colors duration-[180ms] ease-editorial hover:border-mute hover:bg-cream"
                >
                  View order
                  <ExternalLink aria-hidden="true" className="size-3.5 text-mute" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  )
}

/**
 * Pay-by-card: an existing open Stripe hosted invoice opens directly; anything
 * else asks the API to mint (or re-fetch) the link, then shows the 4% fee
 * disclosure BEFORE any redirect — the customer always sees the total first.
 */
function PayByCard({ header }: { header: InvoiceHeader }) {
  const [disclosure, setDisclosure] = useState<PayLinkResponse | null>(null)

  const payMutation = useMutation({
    mutationFn: () => api.createPayLink(header.id),
    onSuccess: (result) => setDisclosure(result),
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'nothing_due') {
        toast('Nothing due on this invoice — it may have just been paid.')
        return
      }
      toast.error('We couldn’t set up card payment. Give it another try.')
    },
  })

  if ((header.balanceDue ?? 0) <= 0) return null

  function pay() {
    // An already-open Stripe hosted invoice keeps its original total — open
    // it directly rather than minting a fresh link.
    if (header.stripeHostedUrl && header.stripeStatus === 'open') {
      window.open(header.stripeHostedUrl, '_blank', 'noopener')
      return
    }
    payMutation.mutate()
  }

  return (
    <>
      <Button onClick={pay} loading={payMutation.isPending}>
        {!payMutation.isPending && <CreditCard aria-hidden="true" />}
        {payMutation.isPending ? 'Setting up…' : 'Pay by card'}
      </Button>
      {disclosure ? (
        <FeeDisclosureDialog
          currency={header.currency}
          disclosure={disclosure}
          onClose={() => setDisclosure(null)}
        />
      ) : null}
    </>
  )
}

function FeeDisclosureDialog({
  currency,
  disclosure,
  onClose,
}: {
  currency: string
  disclosure: PayLinkResponse
  onClose: () => void
}) {
  function proceed() {
    window.open(disclosure.url, '_blank', 'noopener')
    onClose()
  }

  return (
    <Dialog open onClose={onClose} labelledBy="fee-dialog-heading">
      <h2 id="fee-dialog-heading" className="text-lg font-semibold tracking-wordmark text-ink">
        Card payment fee
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-inkMid">
        A 4% card-processing fee applies — your total is{' '}
        <span className="font-mono font-semibold text-ink">
          {formatMoney(disclosure.totalWithFee, currency)}
        </span>{' '}
        ({formatMoney(disclosure.totalWithFee - disclosure.feeAmount, currency)} balance +{' '}
        {formatMoney(disclosure.feeAmount, currency)} fee). Paying by ACH or check avoids the fee.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="quiet" size="sm" onClick={onClose}>
          Not now
        </Button>
        <Button size="sm" onClick={proceed}>
          Continue to payment
        </Button>
      </div>
    </Dialog>
  )
}
